import { Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { GetEmpty } from 'src/app/classes/Empties';
import { NumeroALetras } from 'src/app/classes/NumeroALetras';
import { PurchaseDetailInfo, PurchaseInfo } from 'src/app/models/models';
import { Address, Billing_Data, Currency, Currency_Rate, Store } from 'src/app/models/RestModels';
import { BaseComponent } from '../base/base.component';

@Component({
	selector: 'app-print-purchase',
	templateUrl: './print-purchase.component.html',
	styleUrls: ['./print-purchase.component.css']
})
export class PrintPurchaseComponent extends BaseComponent implements OnInit
{
	purchase_info: PurchaseInfo | null = null;
	billing_data: Billing_Data | null = null;
	provider_address: Address | null = null;
	currency_rate: Currency_Rate | null = null;
	currency: Currency = GetEmpty.currency();
	currency_list: Currency[] = [];
	store: Store | null = null;

	subtotal: number = 0;
	iva: number = 0;
	total: number = 0;
	total_letra: string = '';
	show_iva_column: boolean = false;

	ngOnInit(): void
	{
		this.subs.sink = this.route.paramMap.pipe
		(
			mergeMap((param_map) =>
			{
				this.is_loading = true;
				return forkJoin
				({
					billing_data: this.rest.getBillingData(),
					stores: this.rest.store.search({ limit: 9999 }),
					purchase_info: this.rest.purchase_info.get(param_map.get('id')),
					currency_rate: this.rest.currency_rate.search({ limit: 999999 }),
					currencies: this.rest.currency.search({ limit: 999999 })
				});
			}),
			mergeMap((response) =>
			{
				this.purchase_info = response.purchase_info;
				this.currency_list = response.currencies.data;

				this.store = response.stores.data.find(s => s.id == this.purchase_info.purchase.store_id)
					|| this.purchase_info.store
					|| null;

				if (this.purchase_info && this.store)
				{
					this.purchase_info.store = this.store;
				}

				this.currency_rate = response.currency_rate.data.find((cr) =>
				{
					return this.purchase_info.purchase.store_id == cr.store_id && cr.currency_id == 'USD';
				}) || null;

				let billing_data_id = this.store?.default_billing_data_id || null;
				let billing_data: Billing_Data | undefined = billing_data_id
					? response.billing_data.data.find(bd => bd.id == billing_data_id)
					: undefined;

				let provider_id = this.purchase_info.purchase.provider_user_id;

				return forkJoin
				({
					billing_data: billing_data ? of(billing_data) : (billing_data_id ? this.rest.billing_data.get(billing_data_id) : of(null)),
					provider_address: provider_id ? this.rest.address.search({ limit: 1, eq: { user_id: provider_id, type: 'BILLING' } }) : of({ data: [] as Address[] })
				});
			})
		)
		.subscribe((response) =>
		{
			this.billing_data = response.billing_data || null;
			this.provider_address = response.provider_address.data[0] || null;

			let currency_id = this.purchase_info?.bill?.currency_id || 'MXN';
			this.currency = this.currency_list.find(c => c.id == currency_id) as Currency || GetEmpty.currency();

			let subtotal_acum = 0;
			let iva_acum = 0;
			let total_acum = 0;
			let any_tax = false;

			for (let pdi of this.purchase_info.details)
			{
				let row_total = this.getRowTotal(pdi);
				let row_importe = this.getRowImporte(pdi);
				let row_iva = row_total - row_importe;

				subtotal_acum += row_importe;
				iva_acum += row_iva;
				total_acum += row_total;

				if (row_iva > 0)
					any_tax = true;
			}

			this.subtotal = Math.round(subtotal_acum * 100) / 100;
			this.iva = Math.round(iva_acum * 100) / 100;
			this.total = Math.round(total_acum * 100) / 100;
			this.show_iva_column = any_tax;

			let n = this.currency.id == 'USD'
				? new NumeroALetras('DOLAR', 'DOLARES', '/100 U.S.D.', '/100 U.S.D.')
				: new NumeroALetras('PESO', 'PESOS', '/100 M.N.', '/100 M.N.');
			this.total_letra = n.numeroALetras(this.total);

			this.is_loading = false;
		}, (error) => this.showError(error));
	}

	getItemTaxRate(pdi: PurchaseDetailInfo): number
	{
		let item = pdi.item;
		if (!item)
			return this.store?.tax_percent || 0;

		if (item.applicable_tax === 'EXEMPT')
			return 0;

		if (item.applicable_tax === 'PERCENT')
			return item.tax_percent || 0;

		return this.store?.tax_percent || 0;
	}

	getRowTotal(pdi: PurchaseDetailInfo): number
	{
		let unit_price = pdi.purchase_detail.unitary_price || 0;
		let qty = pdi.purchase_detail.qty || 0;
		return Math.round(unit_price * qty * 100) / 100;
	}

	getRowImporte(pdi: PurchaseDetailInfo): number
	{
		let total_with_tax = this.getRowTotal(pdi);
		let rate = this.getItemTaxRate(pdi);
		if (rate <= 0)
			return total_with_tax;
		return Math.round((total_with_tax / (1 + rate / 100)) * 100) / 100;
	}

	getRowIva(pdi: PurchaseDetailInfo): number
	{
		let row_total = this.getRowTotal(pdi);
		let row_importe = this.getRowImporte(pdi);
		return Math.round((row_total - row_importe) * 100) / 100;
	}

	printDirect()
	{
		window.print();
	}

	goBack()
	{
		this.location.back();
	}
}
